import { useEffect, useState } from "react";
import { getCalendrier } from "@/lib/apiProcedure";
import type { TourCalendrier } from "@/types/procedure";

interface Props {
  caseId: string | number;
  userSide: "DEMANDEUR" | "DEFENDEUR";
}

/**
 * Panneau calendrier pour le cockpit avocat : affiche les tours et explique le
 * contradictoire (AUA art. 9). La visibilité réelle des conclusions adverses
 * est filtrée côté serveur ; ici on explique à l'avocat *pourquoi* il ne les
 * voit pas encore (UX informationnelle, miroir de l'algorithme back).
 */
export function CalendrierAvocat({ caseId, userSide }: Props) {
  const [tours, setTours] = useState<TourCalendrier[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let actif = true;
    getCalendrier(caseId)
      .then((res) => {
        if (actif) setTours(res.data.tours);
      })
      .catch(() => {
        if (actif) setTours([]);
      })
      .finally(() => {
        if (actif) setLoading(false);
      });
    return () => {
      actif = false;
    };
  }, [caseId]);

  if (loading) return null;

  const now = new Date();
  const isExpired = (deadline: string) => new Date(deadline) < now;
  const visibleAdverse = (tour: TourCalendrier) =>
    tour.statut === "CLOS" || tour.statut === "TARDIF" || isExpired(tour.deadline);

  return (
    <div className="border rounded-lg p-4 mt-6 bg-gray-50">
      <h3 className="text-lg font-bold text-gray-800 mb-4">
        Calendrier procédural
      </h3>
      {tours.length === 0 ? (
        <p className="text-sm text-gray-500 italic">
          Aucun tour de calendrier défini par le tribunal pour le moment.
        </p>
      ) : (
        <div className="space-y-4">
          {tours.map((tour) => {
            const isMine = tour.partie_cible === userSide;
            const isOverdue = isExpired(tour.deadline) && tour.statut === "OUVERT";
            return (
              <div
                key={tour.id}
                className={`p-3 border rounded ${isMine ? "border-blue-200 bg-blue-50" : "border-gray-200 bg-white"}`}
              >
                <div className="flex justify-between items-center mb-1">
                  <span className="font-semibold text-sm">
                    {tour.tour_type.replace(/_/g, " ")}
                  </span>
                  <span
                    className={`text-xs px-2 py-1 rounded-full font-bold ${
                      tour.statut === "CLOS"
                        ? "bg-gray-200 text-gray-700"
                        : tour.statut === "TARDIF" || isOverdue
                          ? "bg-red-100 text-red-700"
                          : "bg-green-100 text-green-700"
                    }`}
                  >
                    {isOverdue ? "EN RETARD" : tour.statut}
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  Pour : {tour.partie_cible} | Échéance :{" "}
                  {new Date(tour.deadline).toLocaleString()}
                </p>
                {!isMine && !visibleAdverse(tour) && (
                  <p className="text-xs text-amber-600 mt-2 italic">
                    ⚠️ Conclusions adverses non encore accessibles (égalité des
                    armes : en attente de clôture ou de votre dépôt).
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
